<template>
  <div v-if="cityInfo && shouldShow" class="city-info-section">
    <!-- Activities mode -->
    <template v-if="type === 'activities'">
      <h2 class="city-info-section__title">{{ $t('About Experiences in') }} {{ cityInfo.city_name }}</h2>

      <p class="city-info-section__intro">
        {{ activitiesIntroText }}
      </p>

      <div v-if="cityInfo.hotel_count > 0" class="city-info-section__cross-link">
        <nuxt-link :to="`/hotels/${citySlug}`" class="city-info-section__activities-link">
          {{ $t('Browse') }} {{ cityInfo.hotel_count }} {{ $t('hotels in') }} {{ cityInfo.city_name }}
        </nuxt-link>
      </div>
    </template>

    <!-- Hotels mode (default) -->
    <template v-else>
      <h2 class="city-info-section__title">{{ $t('About Hotels in') }} {{ cityInfo.city_name }}</h2>

      <p class="city-info-section__intro">
        {{ introText }}
      </p>

      <div v-if="cityInfo.top_amenities?.length" class="city-info-section__amenities">
        <span class="city-info-section__amenities-label">{{ $t('Most common amenities') }}:</span>
        <span v-for="(amenity, i) in cityInfo.top_amenities" :key="amenity">
          {{ amenity }}<span v-if="i < cityInfo.top_amenities.length - 1">, </span>
        </span>
      </div>

      <div v-if="cityInfo.poi_pages?.length" class="city-info-section__pois">
        <span class="city-info-section__pois-label">{{ $t('Popular areas') }}:</span>
        <div class="city-info-section__poi-links">
          <nuxt-link
            v-for="poi in cityInfo.poi_pages"
            :key="poi.slug"
            :to="`/hotels/near/${poi.slug}`"
            class="city-info-section__poi-link"
          >
            {{ poi.title }}
          </nuxt-link>
        </div>
      </div>

      <!-- FAQ section -->
      <div v-if="faqItems.length" class="city-info-section__faq">
        <h3 class="city-info-section__faq-title">{{ $t('Frequently Asked Questions') }}</h3>
        <div v-for="(item, i) in faqItems" :key="i" class="city-info-section__faq-item">
          <h4 class="city-info-section__faq-q">{{ item.question }}</h4>
          <p class="city-info-section__faq-a">{{ item.answer }}</p>
        </div>
      </div>

      <div v-if="cityInfo.activities_count > 0" class="city-info-section__activities">
        <nuxt-link :to="`/experiences/${citySlug}`" class="city-info-section__activities-link">
          {{ $t('Explore') }} {{ cityInfo.activities_count }} {{ $t('things to do in') }} {{ cityInfo.city_name }}
        </nuxt-link>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  citySlug: string
  type?: 'hotels' | 'activities'
  preloadedInfo?: any | null
}>()

const config = useRuntimeConfig()
const baseUrl = config.public.baseURL

interface POI {
  title: string
  slug: string
}

interface CityInfo {
  city_name: string
  country_name: string
  hotel_count: number
  price_min: number | null
  price_max: number | null
  avg_rating: number | null
  top_amenities: string[]
  activities_count: number
  poi_pages: POI[]
  ai_description: string | null
  ai_meta_description: string | null
  ai_faq: Array<{ question: string; answer: string }> | null
}

const cityInfo = ref<CityInfo | null>(props.preloadedInfo ?? null)

const shouldShow = computed(() => {
  if (!cityInfo.value) return false
  if (props.type === 'activities') return cityInfo.value.activities_count > 0
  return cityInfo.value.hotel_count > 0
})

const introText = computed(() => {
  if (!cityInfo.value) return ''
  const c = cityInfo.value

  // Prefer AI-generated description
  if (c.ai_description) return c.ai_description

  // Fallback to template-based text
  let text = `${c.city_name} has ${c.hotel_count} hotels available`

  if (c.price_min && c.price_max) {
    text += `, with nightly rates from US$${Math.round(c.price_min)} to US$${Math.round(c.price_max)}`
  }

  if (c.avg_rating) {
    text += `. The average star rating is ${c.avg_rating}`
  }

  text += '.'
  return text
})

const activitiesIntroText = computed(() => {
  if (!cityInfo.value) return ''
  const c = cityInfo.value
  return `Explore ${c.activities_count} tours, activities and things to do in ${c.city_name}, ${c.country_name}.`
})

const faqItems = computed(() => {
  if (!cityInfo.value?.ai_faq?.length) return []
  return cityInfo.value.ai_faq
})

// Only fetch if no preloaded data provided
onMounted(async () => {
  if (cityInfo.value) return
  try {
    const data = await $fetch<CityInfo>(
      `${baseUrl}/api/product/v1/hotels/city-info/${props.citySlug}`
    )
    cityInfo.value = data
  } catch {
    // Non-critical — supplementary content
  }
})
</script>

<style scoped>
.city-info-section {
  margin-top: 32px;
  padding: 24px;
  background-color: var(--light-grey, #f8f9fa);
  border-radius: 12px;
}

.city-info-section__title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
}

.city-info-section__intro {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-secondary, #444);
  margin-bottom: 16px;
}

.city-info-section__amenities {
  font-size: 14px;
  color: var(--text-secondary, #555);
  margin-bottom: 12px;
}

.city-info-section__amenities-label,
.city-info-section__pois-label {
  font-weight: 600;
  color: var(--black, #222);
}

.city-info-section__pois {
  margin-bottom: 12px;
}

.city-info-section__poi-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-top: 6px;
}

.city-info-section__poi-link {
  font-size: 14px;
  color: var(--blue, #1985f9);
  text-decoration: none;
}

.city-info-section__poi-link:hover {
  text-decoration: underline;
}

.city-info-section__faq {
  margin: 20px 0;
  padding-top: 16px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.city-info-section__faq-title {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 12px;
}

.city-info-section__faq-item {
  margin-bottom: 14px;
}

.city-info-section__faq-q {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.city-info-section__faq-a {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary, #444);
}

.city-info-section__cross-link,
.city-info-section__activities {
  margin-top: 16px;
}

.city-info-section__activities-link {
  display: inline-flex;
  align-items: center;
  color: var(--blue, #1985f9);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
}

.city-info-section__activities-link:hover {
  text-decoration: underline;
}
</style>
